import { getSignInUrl } from "@app/config/getServiceUrl";
import { adminQueries, membershipQueries } from "@app/config/queryFactory";
import { useOverlay } from "@base/model/hooks/useOverlay";
import { Button } from "@base/ui/Button/Button";
import { Image } from "@base/ui/Image";
import { fetchUserInfo } from "@features/auth/api/request";
import { isSilverTourHiddenToday } from "@features/membership/lib/utils";
import { SilverTourDialog } from "@features/membership/ui/SilverTourDialog";
import { BaseLayout } from "@layouts/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  const { t, i18n } = useTranslation();
  const { open, close } = useOverlay();

  const { data: userInfo } = useQuery({
    queryKey: ["userInfo"],
    queryFn: fetchUserInfo,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const { data: membership } = useQuery({
    ...membershipQueries.membership(),
    enabled: !!userInfo,
  });

  const { data: adminStatus } = useQuery({
    ...adminQueries.status(),
    enabled: !!userInfo,
  });

  const isActiveSilverMember = membership?.status === "ACTIVE" && membership.plan_description.type === "SILVER";
  // 관리자는 회의실을 쓰는 주체가 아니므로 Silver 멤버용 안내와 예약 진입점을 모두 감춘다.
  const isAdmin = adminStatus?.admin === true;

  useEffect(() => {
    if (isActiveSilverMember && !isAdmin && !isSilverTourHiddenToday()) {
      open(({ isOpen, close }) => <SilverTourDialog isOpen={isOpen} onClose={close} />);
    } else {
      close();
    }
  }, [close, isActiveSilverMember, isAdmin, open]);

  const currentLanguage = i18n.language;

  const navigate = useNavigate();

  return (
    <BaseLayout>
      <section
        id="hero"
        className="relative w-full h-[calc(100vh-58px)] md:h-[calc(100vh-80px)] flex items-center overflow-hidden"
        aria-label="Hero section"
      >
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/82office_hero.png"
            alt="82 Office Hero Image"
            className="object-cover w-full h-full"
            lazy
            sources={[
              { srcSet: "/assets/82office_hero.avif", type: "image/avif" },
              { srcSet: "/assets/82office_hero.webp", type: "image/webp" },
            ]}
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>
        <div className="relative z-10 w-full max-w-[312px] md:max-w-max mx-auto text-white flex flex-col items-center">
          <h1 className="text-[40px] md:text-[60px] leading-tight font-bold whitespace-pre-line md:whitespace-normal text-center">
            {t("heroTitle")}
          </h1>

          <p className="text-gray-10 text-base font-normal leading-[1.3] tracking-[-0.32px] md:text-2xl md:tracking-[-0.48px] text-center mb-6 mt-2 whitespace-pre-line md:whitespace-normal">
            {t("heroDescription")}
          </p>

          {isActiveSilverMember && !isAdmin && (
            <Button variant="outline" asChild className="bg-white">
              <Link to="/reserve">{t("reserveRooms")}</Link>
            </Button>
          )}
          {(!userInfo || !membership || membership.status !== "ACTIVE") && (
            <Button
              variant="outline"
              className="bg-white"
              onClick={() => {
                if (userInfo !== undefined) {
                  navigate({ to: "/apply" });
                  return;
                }

                location.href = getSignInUrl({ locale: currentLanguage, redirectUrl: window.location.href });
              }}
            >
              {t("membershipApplyNow")}
            </Button>
          )}
        </div>

        <a
          href={`https://buildblock.ai/${currentLanguage}/privacy-policy`}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-xs leading-[1.3] tracking-[-0.24px] text-gray-400 hover:text-gray-100"
        >
          Privacy Policy
        </a>
      </section>
    </BaseLayout>
  );
}
